// ══════════════════════════════════════════════════════════════════════════════
// SoundWaveform — 3 modes de rendu + zoom dynamique calculé selon le fichier
//
//  CORRECTIONS :
//  1. Graduation amplitude : labels dessinés DANS la zone de dessin (fond semi-transparent)
//  2. Zoom horizontal : conserve la position de l'ascenseur (centrage sur la vue courante)
//
//  MODE 1 · envelope   visibleSamples > numPixels
//    → min + max par bloc (style Audacity)
//
//  MODE 2 · curve      1 ≤ pixelsPerSample < SAMPLE_THRESHOLD
//    → interpolation linéaire entre samples, lineWidth 2px
//
//  MODE 3 · samples    pixelsPerSample ≥ SAMPLE_THRESHOLD (= 8)
//    → barre 0→valeur + carré au sommet pour chaque sample
//
//  ZOOM_STEPS : puissances de 2 générées dynamiquement jusqu'à ce que
//  le dernier palier garantisse pixelsPerSample ≥ SAMPLE_THRESHOLD
//
//  AMP_ZOOM : zoom vertical sur l'amplitude (la hauteur du composant
//  reste fixe, la forme d'onde est clippée aux bords)
// ══════════════════════════════════════════════════════════════════════════════

import React, {
  useState, useRef, useEffect, useCallback, useMemo,
} from 'react';

// ─── Constantes ───────────────────────────────────────────────────────────────

const HANDLE_PAD       = 14;   // px canvas réservés aux poignées de chaque côté
const HANDLE_HIT       = 10;   // demi-largeur cliquable d'une poignée
const SAMPLE_THRESHOLD = 8;    // px/sample minimum pour le mode échantillons
const CURVE_THRESHOLD  = 0.0625;    // px/sample (passage enveloppe → courbe)

// Paliers de zoom horizontal (puissances de 2)
const BASE_ZOOM_STEPS = [1, 2, 4, 8, 16, 32, 64, 128, 256, 512, 1024, 2048, 4096];

// Paliers de zoom vertical (amplitude)
const AMP_ZOOM_STEPS = [1, 1.5, 2, 3, 4, 6, 8, 12, 16];

// Candidats de pas pour la grille verticale d'amplitude
const AMP_STEP_CANDIDATES = [
  0.001, 0.002, 0.005,
  0.01, 0.02, 0.05,
  0.1, 0.2, 0.25, 0.5, 1.0,
];

// ─── Utilitaires ─────────────────────────────────────────────────────────────

function fmtTimecode(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  const d = Math.floor((sec % 1) * 10);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${d}`;
}

// Prochaine puissance de 2 >= n
function nextPow2(n: number): number {
  let p = 1;
  while (p < n) p *= 2;
  return p;
}

// ─── Types ───────────────────────────────────────────────────────────────────

interface RawPeaks {
  L: Float32Array;
  R: Float32Array | null;
  sampleRate: number;
  totalSamples: number;
}

type RenderMode = 'envelope' | 'curve' | 'samples';

interface DisplayPeaks {
  mode: RenderMode;
  // MODE 1
  mins?: Float32Array;
  maxs?: Float32Array;
  // MODE 2
  vals?: Float32Array;
  // MODE 3
  sampleXs?:   number[];
  sampleVals?: number[];
}

interface SoundWaveformProps {
  url:           string;
  accentColor:   string;
  startPct:      number;
  endPct:        number;
  duration:      number;
  onStartChange: (v: number) => void;
  onEndChange:   (v: number) => void;
  onDurationChange?: (sec: number) => void;
  height?:       number;
}

// ══════════════════════════════════════════════════════════════════════════════
// Composant
// ══════════════════════════════════════════════════════════════════════════════

export const SoundWaveform: React.FC<SoundWaveformProps> = ({
  url, accentColor, startPct, endPct, duration,
  onStartChange, onEndChange, onDurationChange,
  height = 466,
}) => {
  const canvasRef    = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Audio engine
  const audioCtxRef      = useRef<AudioContext | null>(null);
  const sourceRef        = useRef<AudioBufferSourceNode | null>(null);
  const animRef          = useRef<number>(0);
  const decodedRef       = useRef<AudioBuffer | null>(null);
  const playStartTimeRef = useRef<number>(0);
  const playOffsetRef    = useRef<number>(0);

  // Refs stables pour changeZoom (évite closures périmées)
  const cursorPctRef   = useRef<number>(0);
  const playheadPctRef = useRef<number>(0);
  const playingRef     = useRef<boolean>(false);

  const [rawPeaks,    setRawPeaks]    = useState<RawPeaks | null>(null);
  const [loading,     setLoading]     = useState(false);
  const [dragging,    setDragging]    = useState<'start' | 'end' | 'cursor' | null>(null);
  const [cW,          setCW]          = useState(600);
  const [zoomIdx,     setZoomIdx]     = useState(0);
  const [ampZoomIdx,  setAmpZoomIdx]  = useState(0);
  const [scrollPct,   setScrollPct]   = useState(0);
  const [cursorPct,   setCursorPct]   = useState(0);
  const [playing,     setPlaying]     = useState(false);
  const [paused,      setPaused]      = useState(false);
  const [playheadPct, setPlayheadPct] = useState(0);
  const [loop,        setLoop]        = useState(false);
  const loopRef = useRef(false);
  useEffect(() => { loopRef.current = loop; }, [loop]);

  // Helpers : setState + ref en une seule opération (sync garantie, pas de décalage de cycle)
  const setCursorPctSync   = (v: number)  => { cursorPctRef.current   = v; setCursorPct(v);   };
  const setPlayheadPctSync = (v: number)  => { playheadPctRef.current = v; setPlayheadPct(v); };
  const setPlayingSync     = (v: boolean) => { playingRef.current     = v; setPlaying(v);     };

  const innerW  = Math.max(0, cW - HANDLE_PAD * 2);
  const ampZoom = AMP_ZOOM_STEPS[ampZoomIdx];

  // ── ZOOM_STEPS dynamique ─────────────────────────────────────────────────
  const zoomSteps = useMemo(() => {
    if (!rawPeaks || innerW <= 0) return BASE_ZOOM_STEPS;
    const zoomNeeded = (SAMPLE_THRESHOLD * rawPeaks.totalSamples) / innerW;
    const maxZoom    = nextPow2(Math.ceil(zoomNeeded));
    if (maxZoom <= BASE_ZOOM_STEPS[BASE_ZOOM_STEPS.length - 1]) return BASE_ZOOM_STEPS;
    const extra: number[] = [];
    let z = BASE_ZOOM_STEPS[BASE_ZOOM_STEPS.length - 1] * 2;
    while (z <= maxZoom) { extra.push(z); z *= 2; }
    return [...BASE_ZOOM_STEPS, ...extra];
  }, [rawPeaks, innerW]);

  const safeZoomIdx = Math.min(zoomIdx, zoomSteps.length - 1);
  const zoom        = zoomSteps[safeZoomIdx];
  const windowSize  = 100 / zoom;
  const maxScroll   = Math.max(0, 100 - windowSize);

  // ── CORRECTION 2 : suppression du useEffect qui écrasait scrollPct au zoom ──
  // L'ancien useEffect sur [zoomIdx, zoomSteps] ramenait l'ascenseur
  // indépendamment de la logique de changeZoom. Il est supprimé ici.
  // La gestion du clamp est désormais entièrement dans changeZoom.

  // ── Conversions ──────────────────────────────────────────────────────────
  const pctToX = useCallback(
    (pct: number) => HANDLE_PAD + ((pct - scrollPct) / windowSize) * innerW,
    [scrollPct, windowSize, innerW],
  );
  const xToPct = useCallback(
    (x: number) => scrollPct + ((x - HANDLE_PAD) / innerW) * windowSize,
    [scrollPct, windowSize, innerW],
  );
  const pctToSec = useCallback((pct: number) => pct / 100 * duration, [duration]);
  const secToPct = useCallback(
    (sec: number) => duration > 0 ? sec / duration * 100 : 0,
    [duration],
  );

  // ── ResizeObserver ───────────────────────────────────────────────────────
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setCW(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Décodage audio ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!url) { setRawPeaks(null); decodedRef.current = null; return; }
    setLoading(true);
    try { sourceRef.current?.stop(); } catch {}
    sourceRef.current = null;
    cancelAnimationFrame(animRef.current);
    if (audioCtxRef.current) { audioCtxRef.current.close().catch(() => {}); audioCtxRef.current = null; }
    setPlayingSync(false); setPaused(false);
    setCursorPctSync(0); setPlayheadPctSync(0);
    setZoomIdx(0); setScrollPct(0);
    setAmpZoomIdx(0);

    const actx = new AudioContext();
    fetch(url)
      .then(r => r.arrayBuffer())
      .then(buf => actx.decodeAudioData(buf))
      .then(decoded => {
        decodedRef.current = decoded;
        const L = decoded.getChannelData(0);
        const R = decoded.numberOfChannels >= 2 ? decoded.getChannelData(1) : null;
        setRawPeaks({
          L: new Float32Array(L),
          R: R ? new Float32Array(R) : null,
          sampleRate: decoded.sampleRate,
          totalSamples: decoded.length,
        });
        setLoading(false);
        actx.close();
        // Durée réellement décodée (celle utilisée pour la lecture) : source de vérité
        // pour les bornes de la forme d'onde, à préférer à toute durée obtenue via
        // HTMLAudioElement.duration (peu fiable / potentiellement absente).
        onDurationChange?.(decoded.duration);
      })
      .catch(() => { setLoading(false); actx.close(); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  // ── buildDisplayPeaks ────────────────────────────────────────────────────
  const buildDisplayPeaks = useCallback((
    channel: Float32Array,
    numPixels: number,
  ): DisplayPeaks => {
    if (numPixels <= 0 || !rawPeaks) {
      return { mode: 'envelope', mins: new Float32Array(0), maxs: new Float32Array(0) };
    }

    const total           = rawPeaks.totalSamples;
    const startSample     = scrollPct / 100 * total;
    const endSample       = (scrollPct + windowSize) / 100 * total;
    const visibleSamples  = Math.max(1, endSample - startSample);
    const pixelsPerSample = numPixels / visibleSamples;

    // ── MODE 3 : échantillons individuels — PRIORITAIRE ──────────────────
    if (pixelsPerSample >= SAMPLE_THRESHOLD) {
      const sampleXs:   number[] = [];
      const sampleVals: number[] = [];
      const s0 = Math.floor(startSample);
      const s1 = Math.ceil(endSample);
      for (let s = s0; s <= s1 && s < total; s++) {
        const px = ((s - startSample) / visibleSamples) * numPixels;
        if (px >= -1 && px <= numPixels + 1) {
          sampleXs.push(px);
          sampleVals.push(channel[s] ?? 0);
        }
      }
      return { mode: 'samples', sampleXs, sampleVals };
    }

    // ── MODE 2 : courbe interpolée linéairement ──────────────────────────
    if (pixelsPerSample >= CURVE_THRESHOLD) {
      const vals = new Float32Array(numPixels);
      for (let px = 0; px < numPixels; px++) {
        const sF  = startSample + (px / numPixels) * visibleSamples;
        const s0i = Math.floor(sF);
        const s1i = Math.min(total - 1, s0i + 1);
        const t   = sF - s0i;
        const v0  = channel[Math.min(total - 1, s0i)] ?? 0;
        const v1  = channel[s1i] ?? 0;
        vals[px]  = v0 + t * (v1 - v0);
      }
      return { mode: 'curve', vals };
    }

    // ── MODE 1 : enveloppe min/max par bloc (Audacity) ───────────────────
    const mins = new Float32Array(numPixels);
    const maxs = new Float32Array(numPixels);
    const samplesPerPx = visibleSamples / numPixels;
    for (let px = 0; px < numPixels; px++) {
      const s0i = Math.floor(startSample + px       * samplesPerPx);
      const s1i = Math.floor(startSample + (px + 1) * samplesPerPx);
      let mn = 1, mx = -1;
      for (let s = s0i; s < s1i && s < total; s++) {
        const v = channel[s];
        if (v < mn) mn = v;
        if (v > mx) mx = v;
      }
      mins[px] = mn;
      maxs[px] = mx;
    }
    return { mode: 'envelope', mins, maxs };
  }, [rawPeaks, scrollPct, windowSize]);

  // ── Dessin canvas ────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !rawPeaks) return;

    const stereo = !!rawPeaks.R;
    const W = cW, H = height;
    if (W <= 0 || H <= 0 || innerW <= 0) return;

    // ── FIX RETINA : résolution × devicePixelRatio ───────────────────────
    const dpr = window.devicePixelRatio || 1;
    canvas.width  = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    const ctx = canvas.getContext('2d')!;
    ctx.scale(dpr, dpr);

    ctx.fillStyle = '#0a0e1a';
    ctx.fillRect(0, 0, W, H);

    const drawW = innerW;
    const drawX = HANDLE_PAD;

    // ── GRADUATION HORIZONTALE (timecodes) ──────────────────────────────────
    const drawTimeGrid = () => {
      if (duration <= 0) return;

      const visibleSec = (windowSize / 100) * duration;

      const candidates = [
        0.001, 0.002, 0.005,
        0.01, 0.02, 0.05,
        0.1, 0.2, 0.5,
        1, 2, 5, 10, 15, 30,
        60, 120, 300,
      ];
      const TARGET = 8;
      let step = candidates[candidates.length - 1];
      for (const c of candidates) {
        if (visibleSec / c <= TARGET) { step = c; break; }
      }

      const startSec = (scrollPct / 100) * duration;
      const endSec   = startSec + visibleSec;
      const firstTick = Math.ceil(startSec / step) * step;

      ctx.save();
      ctx.font      = '9px monospace';
      ctx.textAlign = 'center';

      for (let t = firstTick; t <= endSec + step * 0.001; t += step) {
        const pct = (t / duration) * 100;
        const x   = Math.round(pctToX(pct)) + 0.5;
        if (x < drawX || x > drawX + drawW) continue;

        ctx.strokeStyle = 'rgba(255,255,255,0.07)';
        ctx.lineWidth   = 1;
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, H);
        ctx.stroke();

        ctx.strokeStyle = 'rgba(255,255,255,0.25)';
        ctx.lineWidth   = 1;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, 5);
        ctx.stroke();

        const label = fmtTimecode(t);
        const tw    = ctx.measureText(label).width;
        if (x - tw / 2 < drawX || x + tw / 2 > drawX + drawW) continue;
        ctx.fillStyle = 'rgba(255,255,255,0.30)';
        ctx.fillText(label, x, 14);
      }
      ctx.restore();
    };

    // ── CORRECTION 1 : GRADUATION VERTICALE (amplitude) ─────────────────────
    // Labels dessinés DANS la zone de dessin avec fond semi-transparent
    // (l'ancienne version écrivait à gauche de drawX dans la zone HANDLE_PAD,
    //  ce qui les rendait invisibles ou très exigus)
    const drawAmpGrid = (yCenter: number, halfH: number, clipTop: number, clipBottom: number) => {
      const ampAtTop    = (yCenter - clipTop)    / halfH;
      const ampAtBottom = (yCenter - clipBottom) / halfH;

      const ampRange = ampAtTop - ampAtBottom;
      const TARGET_TICKS = 8;
      let step = AMP_STEP_CANDIDATES[AMP_STEP_CANDIDATES.length - 1];
      for (const c of AMP_STEP_CANDIDATES) {
        if (ampRange / c <= TARGET_TICKS) { step = c; break; }
      }

      ctx.save();
      ctx.font      = '8px monospace';
      ctx.textAlign = 'left';   // ← labels à l'intérieur, côté gauche

      const firstTick = Math.ceil(ampAtBottom / step) * step;

      for (let amp = firstTick; amp <= ampAtTop + step * 0.001; amp += step) {
        const ampRounded = Math.round(amp / step) * step;
        const y = Math.round(yCenter - ampRounded * halfH) + 0.5;

        if (y < clipTop - 0.5 || y > clipBottom + 0.5) continue;

        const isZero = Math.abs(ampRounded) < step * 0.01;

        // Ligne de grille horizontale pleine largeur
        ctx.strokeStyle = isZero
          ? 'rgba(255,255,255,0.14)'
          : 'rgba(255,255,255,0.06)';
        ctx.lineWidth   = isZero ? 1 : 0.5;
        ctx.setLineDash(isZero ? [4, 6] : [2, 6]);
        ctx.beginPath();
        ctx.moveTo(drawX, y);
        ctx.lineTo(drawX + drawW, y);
        ctx.stroke();
        ctx.setLineDash([]);

        // Petit trait vertical (tick) sur le bord gauche de la zone de dessin
        ctx.strokeStyle = isZero
          ? 'rgba(255,255,255,0.40)'
          : 'rgba(255,255,255,0.22)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(drawX,     y);
        ctx.lineTo(drawX - 5, y);
        ctx.stroke();

        // Label amplitude avec fond semi-transparent DANS la zone de dessin
        const label = isZero
          ? '0'
          : (ampRounded > 0
              ? `+${ampRounded.toFixed(ampRounded < 0.1 ? 3 : 2)}`
              : ampRounded.toFixed(ampRounded > -0.1 ? 3 : 2));

        const tw  = ctx.measureText(label).width;
        const lx  = drawX + 4;           // marge gauche dans la zone de dessin
        const ly  = y - 1;               // ligne de base du texte

        // Fond pour la lisibilité
        ctx.fillStyle = 'rgba(10,14,26,0.72)';
        ctx.fillRect(lx - 1, ly - 8, tw + 4, 10);

        // Texte
        ctx.fillStyle = isZero
          ? 'rgba(255,255,255,0.35)'
          : 'rgba(255,255,255,0.28)';
        ctx.fillText(label, lx, ly);
      }
      ctx.restore();
    };

    const drawChannel = (channel: Float32Array, yCenter: number, halfH: number, clipTop: number, clipBottom: number) => {
      drawAmpGrid(yCenter, halfH, clipTop, clipBottom);

      // Ligne zéro
      const yZero = Math.round(yCenter) + 0.5;
      ctx.save();
      ctx.strokeStyle = 'rgba(255,255,255,0.1)';
      ctx.lineWidth   = 1;
      ctx.setLineDash([4, 6]);
      ctx.beginPath();
      ctx.moveTo(drawX, yZero);
      ctx.lineTo(drawX + drawW, yZero);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();

      const peaks = buildDisplayPeaks(channel, drawW);

      // ── MODE 3 ────────────────────────────────────────────────────────
      if (peaks.mode === 'samples' && peaks.sampleXs && peaks.sampleVals) {
        const SQ = 4 / 3;

        ctx.strokeStyle = accentColor;
        ctx.lineWidth   = 1.5;
        for (let i = 0; i < peaks.sampleXs.length; i++) {
          const px      = drawX + peaks.sampleXs[i];
          const ySample = yCenter - peaks.sampleVals[i] * halfH;
          ctx.beginPath();
          ctx.moveTo(px, yCenter);
          ctx.lineTo(px, ySample);
          ctx.stroke();
        }

        ctx.fillStyle = accentColor;
        for (let i = 0; i < peaks.sampleXs.length; i++) {
          const px      = drawX + peaks.sampleXs[i];
          const ySample = yCenter - peaks.sampleVals[i] * halfH;
          ctx.fillRect(px - SQ, ySample - SQ, SQ * 2, SQ * 2);
        }

        ctx.strokeStyle = accentColor + '66';
        ctx.lineWidth   = 1;
        ctx.beginPath();
        for (let i = 0; i < peaks.sampleXs.length; i++) {
          const px      = drawX + peaks.sampleXs[i];
          const ySample = yCenter - peaks.sampleVals[i] * halfH;
          i === 0 ? ctx.moveTo(px, ySample) : ctx.lineTo(px, ySample);
        }
        ctx.stroke();
        return;
      }

      // ── MODE 2 : courbe 2px, interpolée ──────────────────────────────
      if (peaks.mode === 'curve' && peaks.vals) {
        ctx.fillStyle = accentColor + '22';
        ctx.beginPath();
        ctx.moveTo(drawX, yCenter);
        for (let px = 0; px < drawW; px++) {
          ctx.lineTo(drawX + px, yCenter - peaks.vals[px] * halfH);
        }
        ctx.lineTo(drawX + drawW - 1, yCenter);
        ctx.closePath();
        ctx.fill();

        ctx.strokeStyle = accentColor;
        ctx.lineWidth   = 2;
        ctx.lineJoin    = 'round';
        ctx.beginPath();
        for (let px = 0; px < drawW; px++) {
          const y = yCenter - peaks.vals[px] * halfH;
          px === 0 ? ctx.moveTo(drawX, y) : ctx.lineTo(drawX + px, y);
        }
        ctx.stroke();
        return;
      }

      // ── MODE 1 : enveloppe Audacity ────────────────────────────────────
      if (peaks.mode === 'envelope' && peaks.mins && peaks.maxs) {
        ctx.fillStyle = accentColor + '22';
        ctx.beginPath();
        for (let px = 0; px < drawW; px++) {
          const y = yCenter - peaks.maxs[px] * halfH;
          px === 0 ? ctx.moveTo(drawX + px, y) : ctx.lineTo(drawX + px, y);
        }
        for (let px = drawW - 1; px >= 0; px--) {
          ctx.lineTo(drawX + px, yCenter - peaks.mins[px] * halfH);
        }
        ctx.closePath();
        ctx.fill();

        ctx.strokeStyle = accentColor;
        ctx.lineWidth   = 1;
        ctx.beginPath();
        for (let px = 0; px < drawW; px++) {
          const yMax = yCenter - peaks.maxs[px] * halfH;
          const yMin = yCenter - peaks.mins[px] * halfH;
          ctx.moveTo(drawX + px + 0.5, yMax);
          ctx.lineTo(drawX + px + 0.5, yMin);
        }
        ctx.stroke();
      }
    };

    const drawOverlays = (yCenter: number, halfH: number) => {
      const sx = Math.max(drawX, Math.min(drawX + drawW, pctToX(startPct)));
      const ex = Math.max(drawX, Math.min(drawX + drawW, pctToX(endPct)));
      const top    = Math.max(0, yCenter - halfH);
      const bottom = Math.min(H, yCenter + halfH);
      const h      = bottom - top;
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      if (sx > drawX)         ctx.fillRect(drawX, top, sx - drawX,         h);
      if (ex < drawX + drawW) ctx.fillRect(ex,    top, drawX + drawW - ex, h);
    };

    // Grille temporelle
    drawTimeGrid();

    if (stereo && rawPeaks.R) {
      const baseHalfH = H * 0.25 * 0.86;
      const halfH     = baseHalfH * ampZoom;

      ctx.strokeStyle = 'rgba(255,255,255,0.07)';
      ctx.lineWidth   = 1;
      ctx.beginPath(); ctx.moveTo(0, H / 2); ctx.lineTo(W, H / 2); ctx.stroke();

      // Canal L
      ctx.save();
      ctx.beginPath();
      ctx.rect(drawX, 0, drawW, H / 2);
      ctx.clip();
      drawOverlays(H * 0.25, halfH);
      drawChannel(rawPeaks.L, H * 0.25, halfH, 0, H / 2);
      ctx.restore();

      // Canal R
      ctx.save();
      ctx.beginPath();
      ctx.rect(drawX, H / 2, drawW, H / 2);
      ctx.clip();
      drawOverlays(H * 0.75, halfH);
      drawChannel(rawPeaks.R, H * 0.75, halfH, H / 2, H);
      ctx.restore();

      ctx.font = 'bold 9px monospace'; ctx.fillStyle = 'rgba(255,255,255,0.3)';
      ctx.fillText('L', 5, 13); ctx.fillText('R', 5, H / 2 + 13);
    } else {
      const baseHalfH = H * 0.5 * 0.86;
      const halfH     = baseHalfH * ampZoom;

      ctx.save();
      ctx.beginPath();
      ctx.rect(drawX, 0, drawW, H);
      ctx.clip();
      drawOverlays(H / 2, halfH);
      drawChannel(rawPeaks.L, H / 2, halfH, 0, H);
      ctx.restore();
    }

    // ── Indicateur de clip (bandes en haut/bas si ampZoom > 1) ──
    if (ampZoom > 1) {
      const clipH = 3;
      ctx.fillStyle = accentColor + '40';
      ctx.fillRect(drawX, 0,     drawW, clipH);
      ctx.fillRect(drawX, H - clipH, drawW, clipH);
      if (stereo) {
        ctx.fillRect(drawX, H / 2 - clipH / 2, drawW, clipH);
      }
    }

    // ── Poignées (dessinées hors clip) ────────────────────────────────────
    for (const [pct, color, label] of [
      [startPct, '#ffffff', '◀'],
      [endPct,   '#ffffff', '▶'],
    ] as [number, string, string][]) {
      const x = pctToX(pct);
      if (x < 0 || x > W) continue;
      ctx.fillStyle = color + '25';
      ctx.fillRect(x - HANDLE_HIT, 0, HANDLE_HIT * 2, H);
      ctx.strokeStyle = color + 'cc'; ctx.lineWidth = 3; ctx.setLineDash([]);
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
      ctx.font = 'bold 10px monospace'; ctx.fillStyle = color;
      const tW = ctx.measureText(label).width;
      ctx.fillText(label, x - tW / 2, 12);
    }

    // ── Curseur de lecture ────────────────────────────────────────────────
    const curPct = playing ? playheadPct : cursorPct;
    const curX   = pctToX(curPct);
    if (curX >= 0 && curX <= W) {
      ctx.fillStyle = '#ffffff';
      ctx.beginPath(); ctx.moveTo(curX - 6, 0); ctx.lineTo(curX + 6, 0); ctx.lineTo(curX, 10); ctx.closePath(); ctx.fill();
      ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 1.5; ctx.setLineDash([]);
      ctx.beginPath(); ctx.moveTo(curX, 0); ctx.lineTo(curX, H - 12); ctx.stroke();
      ctx.fillStyle = '#ffffff';
      ctx.beginPath(); ctx.arc(curX, H - 12, 7, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = '#0a0e1a'; ctx.lineWidth = 1.5; ctx.stroke();
    }

    // ── Timecodes ─────────────────────────────────────────────────────────
    if (duration > 0) {
      ctx.font = '10px monospace';
      for (const [pct, tcLabel] of [
        [startPct, fmtTimecode(pctToSec(startPct))],
        [endPct,   fmtTimecode(pctToSec(endPct))],
      ] as [number, string][]) {
        const x = pctToX(pct);
        if (x < 0 || x > W) continue;
        const tW = ctx.measureText(tcLabel).width;
        const tx = Math.max(2, Math.min(W - tW - 4, x - tW / 2));
        ctx.fillStyle = 'rgba(0,0,0,0.7)';
        ctx.fillRect(tx - 2, H - 16, tW + 4, 14);
        ctx.fillStyle = accentColor;
        ctx.fillText(tcLabel, tx, H - 5);
      }
    }

  }, [rawPeaks, startPct, endPct, accentColor, duration, cW, height,
      scrollPct, windowSize, cursorPct, playheadPct, playing, ampZoom,
      buildDisplayPeaks, pctToX, innerW, pctToSec]);

  // ── Playback ─────────────────────────────────────────────────────────────

  const stopPlayback = useCallback(() => {
    try { sourceRef.current?.stop(); } catch {}
    sourceRef.current = null;
    cancelAnimationFrame(animRef.current);
    if (audioCtxRef.current) { audioCtxRef.current.close().catch(() => {}); audioCtxRef.current = null; }
    setPlayingSync(false); setPaused(false);
  }, []);

  const play = useCallback(() => {
    if (!decodedRef.current || !duration) return;

    if (paused && audioCtxRef.current?.state === 'suspended') {
      audioCtxRef.current.resume().then(() => {
        setPlayingSync(true); setPaused(false);
        const actx = audioCtxRef.current!;
        playStartTimeRef.current = actx.currentTime - (pctToSec(playheadPct) - playOffsetRef.current);
        const animate = () => {
          const nowSec = playOffsetRef.current + actx.currentTime - playStartTimeRef.current;
          const nowPct = secToPct(nowSec);
          setPlayheadPctSync(nowPct);
          if (windowSize < 100) {
            const ideal = nowPct - windowSize / 2;
            setScrollPct(Math.max(0, Math.min(maxScroll, ideal)));
          }
          if (nowSec < pctToSec(endPct)) animRef.current = requestAnimationFrame(animate);
          else { stopPlayback(); setCursorPctSync(endPct); }
        };
        animRef.current = requestAnimationFrame(animate);
      });
      return;
    }

    stopPlayback();
    const actx = new AudioContext();
    audioCtxRef.current = actx;
    const src = actx.createBufferSource();
    src.buffer = decodedRef.current;
    src.connect(actx.destination);

    const startSec = pctToSec(cursorPct);
    const endSec   = pctToSec(endPct);
    const offset   = Math.max(pctToSec(startPct), Math.min(startSec, endSec));
    const dur      = endSec - offset;
    if (dur <= 0) { actx.close(); return; }

    playOffsetRef.current    = offset;
    playStartTimeRef.current = actx.currentTime;
    src.start(0, offset, dur);
    sourceRef.current = src;
    setPlayingSync(true); setPaused(false);

    src.onended = () => {
      cancelAnimationFrame(animRef.current);
      if (loopRef.current) {
        // Relancer depuis startPct
        setCursorPctSync(secToPct(pctToSec(startPct)));
        setPlayheadPctSync(secToPct(pctToSec(startPct)));
        // On utilise setTimeout(0) pour laisser React mettre à jour cursorPct
        // avant le prochain appel à play — on relance directement sans setState
        const loopPlay = () => {
          if (!decodedRef.current || !loopRef.current) return;
          const lActx = new AudioContext();
          audioCtxRef.current = lActx;
          const lSrc = lActx.createBufferSource();
          lSrc.buffer = decodedRef.current;
          lSrc.connect(lActx.destination);
          const lOffset = pctToSec(startPct);
          const lEnd    = pctToSec(endPct);
          const lDur    = lEnd - lOffset;
          if (lDur <= 0) { lActx.close(); return; }
          playOffsetRef.current    = lOffset;
          playStartTimeRef.current = lActx.currentTime;
          lSrc.start(0, lOffset, lDur);
          sourceRef.current = lSrc;
          setPlayingSync(true); setPaused(false);
          lSrc.onended = () => {
            cancelAnimationFrame(animRef.current);
            if (loopRef.current) { loopPlay(); }
            else { setPlayingSync(false); setPaused(false); setCursorPctSync(secToPct(lEnd)); }
          };
          const loopAnimate = () => {
            const nowSec = playOffsetRef.current + lActx.currentTime - playStartTimeRef.current;
            const nowPct = secToPct(nowSec);
            setPlayheadPctSync(nowPct);
            if (windowSize < 100) {
              const ideal = nowPct - windowSize / 2;
              setScrollPct(Math.max(0, Math.min(maxScroll, ideal)));
            }
            if (nowSec < lEnd) animRef.current = requestAnimationFrame(loopAnimate);
          };
          animRef.current = requestAnimationFrame(loopAnimate);
        };
        loopPlay();
      } else {
        setPlayingSync(false); setPaused(false);
        setCursorPctSync(secToPct(endSec));
      }
    };

    const animate = () => {
      const nowSec = playOffsetRef.current + actx.currentTime - playStartTimeRef.current;
      const nowPct = secToPct(nowSec);
      setPlayheadPctSync(nowPct);
      // Suivi du curseur : on centre la fenêtre sur la tête de lecture
      // uniquement quand le zoom est actif (windowSize < 100)
      if (windowSize < 100) {
        const ideal = nowPct - windowSize / 2;
        setScrollPct(Math.max(0, Math.min(maxScroll, ideal)));
      }
      if (nowSec < pctToSec(endPct)) animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
  }, [cursorPct, startPct, endPct, duration, stopPlayback, windowSize,
      maxScroll, paused, playheadPct, pctToSec, secToPct]);

  const pause = useCallback(() => {
    if (!playing || !audioCtxRef.current) return;
    cancelAnimationFrame(animRef.current);
    const nowSec = playOffsetRef.current + audioCtxRef.current.currentTime - playStartTimeRef.current;
    setCursorPctSync(secToPct(nowSec));
    setPlayheadPctSync(secToPct(nowSec));
    audioCtxRef.current.suspend().then(() => { setPlayingSync(false); setPaused(true); });
  }, [playing, secToPct]);

  const stop = useCallback(() => {
    stopPlayback();
    const s = pctToSec(startPct);
    setCursorPctSync(secToPct(s)); setPlayheadPctSync(secToPct(s));
  }, [stopPlayback, startPct, pctToSec, secToPct]);

  useEffect(() => () => { stopPlayback(); }, [stopPlayback]);

  // ── Interactions souris ───────────────────────────────────────────────────

  const getCanvasX = useCallback((e: MouseEvent | React.MouseEvent): number => {
    const canvas = canvasRef.current;
    if (!canvas) return 0;
    const rect = canvas.getBoundingClientRect();
    return Math.max(0, Math.min(cW, (e.clientX - rect.left) * (cW / rect.width)));
  }, [cW]);

  const onCanvasMouseDown = useCallback((e: React.MouseEvent) => {
    const x   = getCanvasX(e);
    const pct = xToPct(x);
    const startX  = pctToX(startPct);
    const endX    = pctToX(endPct);
    const cursorX = pctToX(playing ? playheadPct : cursorPct);

    const doDrag = (handle: 'start' | 'end' | 'cursor') => {
      e.preventDefault();
      setDragging(handle);
      const move = (ev: MouseEvent) => {
        const cpct = xToPct(getCanvasX(ev));
        if (handle === 'start')  onStartChange(Math.max(0,   Math.min(cpct, endPct   - 0.1)));
        if (handle === 'end')    onEndChange(  Math.min(100, Math.max(cpct, startPct + 0.1)));
        if (handle === 'cursor') {
          if (playing) pause();
          const clamped = Math.max(startPct, Math.min(endPct, cpct));
          setCursorPctSync(clamped); setPlayheadPctSync(clamped);
        }
      };
      const up = () => {
        setDragging(null);
        window.removeEventListener('mousemove', move);
        window.removeEventListener('mouseup',   up);
      };
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup',   up);
    };

    const mouseY = (() => {
      const canvas = canvasRef.current;
      if (!canvas) return 0;
      const rect = canvas.getBoundingClientRect();
      return (e.clientY - rect.top) * (height / rect.height);
    })();
    const nearCursorBall = Math.abs(x - cursorX) < 12 && Math.abs(mouseY - (height - 12)) < 12;
    const nearCursorLine = Math.abs(x - cursorX) < HANDLE_HIT;
    const nearStart      = Math.abs(x - startX)  < HANDLE_HIT;
    const nearEnd        = Math.abs(x - endX)     < HANDLE_HIT;

    if      (nearStart && !nearEnd)            doDrag('start');
    else if (nearEnd   && !nearStart)          doDrag('end');
    else if (nearCursorBall || nearCursorLine) doDrag('cursor');
    else if (nearStart)                        doDrag('start');
    else if (nearEnd)                          doDrag('end');
    else {
      if (playing) pause();
      const clamped = Math.max(startPct, Math.min(endPct, pct));
      setCursorPctSync(clamped); setPlayheadPctSync(clamped);
    }
  }, [getCanvasX, xToPct, pctToX, startPct, endPct, playing, playheadPct,
      cursorPct, onStartChange, onEndChange, pause, height]);

  // ── CORRECTION 2 : Zoom horizontal — conserve la position de l'ascenseur ──
  // On centre le zoom sur le milieu de la fenêtre actuellement visible.
  // Le useEffect précédent (sur zoomIdx/zoomSteps) qui ramenait scrollPct
  // vers 0 a été supprimé. Toute la logique est ici.
  const changeZoom = useCallback((dir: 1 | -1) => {
    setZoomIdx(prev => {
      const ni   = Math.max(0, Math.min(zoomSteps.length - 1, prev + dir));
      const nWin = 100 / zoomSteps[ni];
      // Centrer sur la tête de lecture si en cours, sinon sur le curseur
      const anchor = playingRef.current ? playheadPctRef.current : cursorPctRef.current;
      const newScroll = Math.max(0, Math.min(100 - nWin, anchor - nWin / 2));
      setScrollPct(newScroll);
      return ni;
    });
  }, [zoomSteps]);

  const resetZoom = useCallback(() => {
    setZoomIdx(0);
    setScrollPct(0);
  }, []);

  // ── Zoom vertical (amplitude) ────────────────────────────────────────────
  const changeAmpZoom = useCallback((dir: 1 | -1) => {
    setAmpZoomIdx(prev => Math.max(0, Math.min(AMP_ZOOM_STEPS.length - 1, prev + dir)));
  }, []);

  const resetAmpZoom = useCallback(() => {
    setAmpZoomIdx(0);
  }, []);

  // ── Libellé mode courant ─────────────────────────────────────────────────
  const renderModeLabel = useMemo(() => {
    if (!rawPeaks || innerW <= 0) return '';
    const total          = rawPeaks.totalSamples;
    const startSample    = scrollPct / 100 * total;
    const endSample      = (scrollPct + windowSize) / 100 * total;
    const visibleSamples = Math.max(1, endSample - startSample);
    const pps            = innerW / visibleSamples;
    if (pps >= SAMPLE_THRESHOLD)  return '· ◈ samples';
    if (pps >= CURVE_THRESHOLD)   return '· ∿ courbe';
    return '· ▬ enveloppe';
  }, [rawPeaks, innerW, scrollPct, windowSize]);

  const stereo       = !!rawPeaks?.R;

  const ampZoomLabel = ampZoom % 1 === 0 ? `A×${ampZoom}` : `A×${ampZoom.toFixed(1)}`;

  const btnStyle = (active?: boolean, color?: string): React.CSSProperties => ({
    width: 32, height: 32, borderRadius: 6, cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: active ? (color ?? accentColor) + '30' : 'rgba(255,255,255,0.06)',
    border: `1px solid ${active ? (color ?? accentColor) + '80' : 'rgba(255,255,255,0.12)'}`,
    color: active ? (color ?? accentColor) : 'rgba(255,255,255,0.7)',
    flexShrink: 0,
  });

  // ── Rendu ────────────────────────────────────────────────────────────────

  if (!url) return (
    <div style={{ width: '100%', height, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6 }}>
      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.2)', fontStyle: 'italic' }}>Aucun fichier audio chargé</span>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>

      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>

        {/* ── Transport ── */}
        <button type="button" title="Play" onClick={play} disabled={playing && !paused} style={btnStyle((playing && !paused) || paused, '#4ade80')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><path d="M3 2l9 5-9 5V2z"/></svg>
        </button>
        <button type="button" title={paused ? 'Reprendre' : 'Pause'} onClick={playing ? pause : (paused ? play : undefined)} disabled={!playing && !paused} style={btnStyle(playing || paused, '#ffffff')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="2.5" y="2" width="3" height="10" rx="1"/><rect x="8.5" y="2" width="3" height="10" rx="1"/></svg>
        </button>
        <button type="button" title="Stop" onClick={stop} style={btnStyle(false, '#f87171')}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="2" y="2" width="10" height="10" rx="1.5"/></svg>
        </button>

        {/* ── Boucle ── */}
        <button
          type="button"
          title={loop ? 'Boucle activée' : 'Boucle désactivée'}
          onClick={() => setLoop(v => !v)}
          style={btnStyle(loop, '#a78bfa')}
        >
          {/* Icône loop : deux flèches circulaires */}
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 5a5 5 0 0 1 9-1.5"/>
            <path d="M12 9a5 5 0 0 1-9 1.5"/>
            <polyline points="11,2 11.5,4.5 9,4.5"/>
            <polyline points="3,11.5 2.5,9 5,9"/>
          </svg>
        </button>

        <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.1)', flexShrink: 0 }} />

        {/* ── Zoom horizontal ── */}
        <button type="button" title="Dézoomer (horizontal)" onClick={() => changeZoom(-1)} disabled={safeZoomIdx === 0} style={btnStyle(false)}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <circle cx="6" cy="6" r="4"/><line x1="9.5" y1="9.5" x2="12.5" y2="12.5"/><line x1="4" y1="6" x2="8" y2="6"/>
          </svg>
        </button>

        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', fontFamily: 'monospace', minWidth: 64, textAlign: 'center' }}>
          ×{zoom.toLocaleString()}
        </span>

        <button type="button" title="Zoomer (horizontal)" onClick={() => changeZoom(1)} disabled={safeZoomIdx === zoomSteps.length - 1} style={btnStyle(false)}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
            <circle cx="6" cy="6" r="4"/><line x1="9.5" y1="9.5" x2="12.5" y2="12.5"/>
            <line x1="4" y1="6" x2="8" y2="6"/><line x1="6" y1="4" x2="6" y2="8"/>
          </svg>
        </button>

        <button type="button" title="Vue complète" onClick={resetZoom} disabled={safeZoomIdx === 0}
          style={{ ...btnStyle(false), fontSize: 12, fontFamily: 'monospace', fontWeight: 700, color: safeZoomIdx === 0 ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.7)', width: 'auto', padding: '0 8px', minWidth: 32 }}>
          ×1
        </button>

        <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.1)', flexShrink: 0 }} />

        {/* ── Zoom vertical (amplitude) ── */}
        <button type="button" title="Réduire zoom amplitude" onClick={() => changeAmpZoom(-1)} disabled={ampZoomIdx === 0} style={btnStyle(false)}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="4,3 7,6 10,3"/>
            <polyline points="4,11 7,8 10,11"/>
          </svg>
        </button>

        <span style={{ fontSize: 11, color: ampZoomIdx > 0 ? accentColor : 'rgba(255,255,255,0.5)', fontFamily: 'monospace', minWidth: 48, textAlign: 'center' }}>
          {ampZoomLabel}
        </span>

        <button type="button" title="Augmenter zoom amplitude" onClick={() => changeAmpZoom(1)} disabled={ampZoomIdx === AMP_ZOOM_STEPS.length - 1} style={btnStyle(false)}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="4,6 7,3 10,6"/>
            <polyline points="4,8 7,11 10,8"/>
          </svg>
        </button>

        <button type="button" title="Amplitude normale" onClick={resetAmpZoom} disabled={ampZoomIdx === 0}
          style={{ ...btnStyle(false), fontSize: 10, fontFamily: 'monospace', fontWeight: 700, color: ampZoomIdx === 0 ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.7)', width: 'auto', padding: '0 8px', minWidth: 32 }}>
          A×1
        </button>

        <div style={{ flex: 1 }} />

        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', fontFamily: 'monospace' }}>
          {stereo ? 'L / R' : 'MONO'}
          {rawPeaks ? ` · ${rawPeaks.sampleRate / 1000}kHz` : ''}
          {' '}{renderModeLabel}
          {paused ? ' · ⏸' : ''}
        </span>
      </div>

      {/* Canvas */}
      <div ref={containerRef} style={{ width: '100%', position: 'relative', userSelect: 'none', cursor: dragging ? 'ew-resize' : 'crosshair' }}>
        <canvas
          ref={canvasRef}
          style={{ width: '100%', height, display: 'block', borderRadius: 6, border: '1px solid rgba(255,255,255,0.08)' }}
          onMouseDown={onCanvasMouseDown}
        />
        {loading && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(10,14,26,0.8)', borderRadius: 6 }}>
            <span style={{ fontSize: 12, color: accentColor, fontFamily: 'monospace' }}>Décodage audio…</span>
          </div>
        )}

      </div>

      {/* Ascenseur + mini-carte */}
      {zoom > 1 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <input type="range" min={0} max={maxScroll} step={0.001} value={scrollPct}
            onChange={e => setScrollPct(+e.target.value)}
            style={{ width: '100%', accentColor, cursor: 'pointer' }}
          />
          <div style={{ position: 'relative', width: '100%', height: 7, background: 'rgba(255,255,255,0.06)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${scrollPct}%`, width: `${windowSize}%`, background: accentColor + '30', borderRadius: 3 }} />
            <div style={{ position: 'absolute', top: 1, bottom: 1, left: `${startPct}%`, width: `${Math.max(0.3, endPct - startPct)}%`, background: accentColor + '90', borderRadius: 2 }} />
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${playing ? playheadPct : cursorPct}%`, width: 1.5, background: '#ffffff' }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'rgba(255,255,255,0.22)', fontFamily: 'monospace' }}>
            <span>{fmtTimecode(scrollPct / 100 * duration)}</span>
            <span>{fmtTimecode((scrollPct + windowSize) / 100 * duration)}</span>
          </div>
        </div>
      )}

    </div>
  );
};

export default SoundWaveform;